import React from 'react';
import { motion } from 'framer-motion';
import { Bed as BedIcon, UserPlus } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { PilgrimCard } from './PilgrimCard';
import { AccommodationTable, Room, BedAssignment } from '../../../types/accommodation';

type Props = {
  beds: {
    a?: BedAssignment[];
  }[];
  selectedBed: number | null;
  onSelectBed: (index: number) => void;
  onRemove: (bedIndex: number, pilgrimId: string) => Promise<void>;
  userRole: string;
  userId: string;
  hotelName: string;
  hotelType: 'mainMakkah' | 'shiftingMakkah' | 'madinah';
  allRoomAssignments?: BedAssignment[];
};

export const BedList = ({
  beds,
  selectedBed,
  onSelectBed,
  onRemove,
  userRole,
  userId,
  hotelName,
  hotelType,
  allRoomAssignments = []
}: Props) => {
  const { translations } = useLanguage();

  // Check if user can manage this bed's assignments
  const canManageBed = (assignment: BedAssignment): boolean => {
    if (!userId) return false;
    if (userRole === 'admin') return true;
    if (userRole === 'guide') return assignment.gid === userId;
    if (userRole === 'organizer') return assignment.oid === userId;
    return false;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 beds-container">
      {beds.map((bed, index) => {
        // Get current assignments
        const assignments = bed.a || [];
        const latestAssignment = assignments.length > 0 ? assignments[assignments.length - 1] : null;
        const hasOtherAssignments = assignments.length > 1;
        const isStaffBed = latestAssignment?.isStaff;

        return (
          <motion.div
            key={`bed-${index}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => onSelectBed(index)}
            className={`
              relative rounded-lg transition-all transform cursor-pointer
              ${selectedBed === index ? 'scale-[1.02] shadow-md' : 'hover:scale-[1.01] shadow-sm'}
              ${latestAssignment ? (
                isStaffBed ? 'bg-gray-50 border-2 border-gray-300' :
                latestAssignment.g === 'F' ? 'bg-pink-50 border-2 border-pink-300' :
                'bg-blue-50 border-2 border-blue-300'
              ) : 'bg-white border-2 border-dashed border-gray-300 hover:border-primary-300'}
              ${selectedBed === index ? 'ring-4 ring-primary-300' : ''}
            `}
          >
            {/* Bed Header */}
            <div className="bg-primary-100 px-4 py-2 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-primary-800 font-medium">
                <BedIcon size={18} className="text-primary-600" />
                <span className="text-sm">
                  {translations?.bedList?.bed || 'Bed'} {index + 1}
                </span>
              </div>
              {selectedBed === index && (
                <span className="bg-primary-200 text-primary-800 text-xs px-2 py-1 rounded-full">
                  Selected
                </span>
              )}
            </div>

            {/* Bed Content */}
            <div className="p-4">
              {latestAssignment ? (
                <div className="h-full">
                  <PilgrimCard
                    assignments={assignments}
                    bedIndex={index}
                    hotelName={hotelName}
                    hotelType={hotelType}
                    onRemove={(pilgrimId) => {
                      // Find the specific assignment for this pilgrim
                      const assignment = assignments.find(a => a.pid === pilgrimId);
                      // Only allow removal if user has permission for this specific assignment
                      if (assignment && canManageBed(assignment)) {
                        return onRemove(index, pilgrimId);
                      }
                      return Promise.resolve();
                    }}
                    onAssignNew={() => onSelectBed(index)}
                    allRoomAssignments={allRoomAssignments}
                    userRole={userRole}
                    userId={userId}
                  />
                </div>
              ) : (
                <div className="min-h-[200px] flex flex-col items-center justify-center gap-4 text-center p-4">
                  <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center">
                    <BedIcon size={32} className="text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg text-gray-800 mb-1">
                      {translations?.bedList?.available || 'Available for Assignment'}
                    </h3>
                    <p className="text-gray-500 text-sm">
                      Click to select this bed and assign a pilgrim
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="mt-2 w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <UserPlus size={18} />
                    <span>{translations?.bedList?.assign || 'Assign Pilgrim'}</span>
                  </motion.button>
                </div>
              )}
            </div>
            
            {/* Selection Indicator */}
            {selectedBed === index && (
              <motion.div
                layoutId="selection-indicator"
                className="absolute inset-0 border-2 border-primary-500 rounded-lg pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            )}
          </motion.div>
        );
      })}
    </div>
  );
};
